// A success test at the table (issue 1523; docs/MRCS/shadowrun-3.md §3.2.2, §3.6, §5.1): a character's skill or
// attribute, or a typed rating, thrown as that many dice against a target number (TN) the request gives, with its TN
// modifiers; the faces by die chain, the successes, and a disaster when every die shows 1. An open test throws the rating
// with no TN and keeps the highest chain. An opposed test throws a second side beside the first, each with its own dice
// and TN, and names who won; a tie is no result.
//
// Defaulting (§3.2.2, p. 84–85): a skill the character lacks may be tested through a skill it has (the system's `skill`
// route, its TN modifier), a specialization of one (`specialization`) or the linked attribute (`attribute`), and only
// through those the skill's content links name (`defaults_to` to skills of its own family, `defaults_to_attribute` to
// another's). A TN, before the route's own modifier, at or over the system's limit refuses every route. The route's pool
// cap is stamped, and caps the pool dice the test may take (issue 1524).
//
// Pool dice (issue 1524; §5.2): a test of a combatant's token may add dice from one of its pools, at most the pool's cap
// for the kind of test and the route's, and never more than is left. The server checks them (`poolSpendOf`) and
// stamps the spend on the side; the side's dice are its rating and the pool dice, and the working line says both:
// "Stealth 5 + 2 Combat Pool = 7 dice vs TN 5".
//
// The rules are the system's (`features.combat.test`), stamped as the record's `rules`; a system with none has no Test.
// Nothing here throws a die on its own: the server hands the generator in, a test a scripted list.
import { MAX_POOL, parsePool, poolDisaster, poolFaces, poolResult, poolText, testDice, throwPool } from '../dice/pool.js';
import { parseTestDice, POOL_FOR, POOL_USES } from './system.js';
import { conjureLines, parseConjureFacts } from './conjure.js';
import { matrixLines, parseMatrixFacts } from './matrix.js';
import { parseVehicleFacts, vehicleLines } from './vehicle.js';
import { parsePowerFacts, powerLines } from './power.js';
export const RATED_KINDS = ['skill', 'specialization', 'attribute', 'typed'];
const ROUTE_TO = ['skill', 'specialization', 'attribute'];
const ROUTE_POOL = ['half_rating', 'half_base', 'none'];
export const TEST_LIMITS = { name: 80, rating: MAX_POOL, target: 100, modifiers: 8, modifier: 50, label: 40 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A content address as the build stores it: `skill/assault-rifles`, `attribute/quickness`. */
export const ADDRESS_RE = /^[a-z][a-z0-9_]{0,62}\/[a-z0-9][a-z0-9-]{0,127}$/;
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !LINE_BAD.test(v);
const only = (v, keys) => Object.keys(v).every((k) => keys.includes(k));
function parseRated(v) {
    if (!isObj(v) || !only(v, ['kind', 'name', 'rating', 'ref']))
        return null;
    if (!RATED_KINDS.includes(v.kind) || !isText(v.name, TEST_LIMITS.name) || !isInt(v.rating, 1, TEST_LIMITS.rating))
        return null;
    if (v.ref !== undefined && (typeof v.ref !== 'string' || !ADDRESS_RE.test(v.ref) || v.kind === 'typed'))
        return null;
    return { kind: v.kind, name: v.name.trim(), rating: v.rating, ...(typeof v.ref === 'string' ? { ref: v.ref } : {}) };
}
function parseTerms(v, most = TEST_LIMITS.modifiers) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || v.length > most)
        return null;
    const out = [];
    for (const x of v) {
        if (!isObj(x) || !only(x, ['label', 'value']) || !isText(x.label, TEST_LIMITS.label) || !isInt(x.value, -TEST_LIMITS.modifier, TEST_LIMITS.modifier))
            return null;
        out.push({ label: x.label.trim(), value: x.value });
    }
    return out;
}
function parseDefaultAsk(v) {
    if (!isObj(v) || typeof v.skill !== 'string' || !ADDRESS_RE.test(v.skill) || !isText(v.name, TEST_LIMITS.name) || !ROUTE_TO.includes(v.to))
        return null;
    if (v.base !== undefined && (v.to !== 'specialization' || !isInt(v.base, 1, TEST_LIMITS.rating)))
        return null;
    return { skill: v.skill, name: v.name.trim(), to: v.to, ...(v.base !== undefined ? { base: v.base } : {}) };
}
/** Pool dice asked for (issue 1524): a pool's key, at least one die, and a kind of test from the closed list. */
function parsePoolAsk(v) {
    if (!isObj(v) || !only(v, ['key', 'dice', 'for']) || typeof v.key !== 'string' || !POOL_KEY_RE.test(v.key) || !isInt(v.dice, 1, TEST_LIMITS.rating) || !POOL_FOR.includes(v.for))
        return null;
    return { key: v.key, dice: v.dice, for: v.for };
}
const POOL_KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
/** A test request from the wire: exact shape or null. Untrusted input, always. */
export function parseTestRequest(v) {
    if (!isObj(v) || v.kind !== 'test.request')
        return null;
    if (!only(v, ['kind', 'token', 'name', 'rated', 'defaulted', 'target', 'modifiers', 'open', 'opposed', 'private', 'pool', 'resist']))
        return null;
    if (v.resist !== undefined && v.resist !== true)
        return null;
    if (v.token !== undefined && !UUID_RE.test(String(v.token)))
        return null;
    if (v.name !== undefined && (!isText(v.name, TEST_LIMITS.name) || v.token !== undefined))
        return null;
    const rated = parseRated(v.rated);
    if (!rated)
        return null;
    const open = v.open === undefined ? false : v.open;
    const priv = v.private === undefined ? false : v.private;
    if (typeof open !== 'boolean' || typeof priv !== 'boolean')
        return null;
    const modifiers = parseTerms(v.modifiers);
    if (!modifiers)
        return null;
    // An open test has no TN, so nothing to modify; any other names one.
    if (open ? v.target !== undefined || modifiers.length > 0 : !isInt(v.target, 1, TEST_LIMITS.target))
        return null;
    const defaulted = v.defaulted === undefined ? null : parseDefaultAsk(v.defaulted);
    if (v.defaulted !== undefined && !defaulted)
        return null;
    // A default is taken to a rating of the sheet's, never a typed one, and of the kind the route names.
    if (defaulted && (rated.kind === 'typed' || rated.kind !== defaulted.to))
        return null;
    let opposed;
    if (v.opposed !== undefined) {
        const o = v.opposed;
        if (open || !isObj(o) || !only(o, ['name', 'rating', 'target', 'modifiers']) || !isText(o.name, TEST_LIMITS.name) || !isInt(o.rating, 1, TEST_LIMITS.rating) || !isInt(o.target, 1, TEST_LIMITS.target))
            return null;
        const terms = parseTerms(o.modifiers);
        if (!terms)
            return null;
        opposed = { name: o.name.trim(), rating: o.rating, target: o.target, modifiers: terms };
    }
    // Pool dice are a combatant's: only a token's test may spend them.
    const pool = v.pool === undefined ? null : parsePoolAsk(v.pool);
    if (v.pool !== undefined && (!pool || typeof v.token !== 'string' || rated.rating + pool.dice > TEST_LIMITS.rating))
        return null;
    return {
        kind: 'test.request', ...(typeof v.token === 'string' ? { token: v.token } : {}), ...(typeof v.name === 'string' ? { name: v.name.trim() } : {}),
        rated, ...(defaulted ? { defaulted } : {}), ...(open ? {} : { target: v.target }), modifiers, open, ...(opposed ? { opposed } : {}), private: priv,
        ...(pool ? { pool } : {}), ...(v.resist === true ? { resist: true } : {}),
    };
}
/** The word a wound's TN modifier is shown with (issue 1526): "TN 5 (4 + 1 wounded)". */
export const WOUND_TERM = 'wounded';
/** Whether a test is a damage resistance test (issue 1526; p. 126): said so, or drawing pool dice to resist. */
export const isResistance = (req) => req.resist === true || req.pool?.for === 'resist';
/**
 * A test with the tested creature's wound modifier joined to its TN (issue 1526; §5.4): every test but a damage
 * resistance test, and an open test, which has no TN. The term is the server's, after the asker's own (so it may be one
 * more than they may write); none when the creature is unhurt.
 */
export function withWounds(req, target) {
    if (!target || req.open || isResistance(req))
        return req;
    const value = Math.max(-TEST_LIMITS.modifier, Math.min(TEST_LIMITS.modifier, target));
    return { ...req, modifiers: [...req.modifiers, { label: WOUND_TERM, value }] };
}
// ── The rules, stamped ────────────────────────────────────────────────────
/** A test's dice back in the data's words (`features.combat.test.dice`), as the record stamps them. */
export function testDiceData(t) {
    return {
        count: t.count, sides: t.sides,
        ...(t.explode ? { explode: { on: t.explode.on, limit: t.explode.limit, ...(t.explode.aboveTarget ? { above_target: true } : {}) } } : {}),
        ...(t.keep ? { keep: { [t.keep.which]: t.keep.n } } : {}),
        ...(t.success ? { success: {
                at_least: t.success.atLeast, modifiers: t.success.modifiers, ...(t.success.minTarget !== undefined ? { min_target: t.success.minTarget } : {}),
                ...(t.success.chains ? { chains: t.success.chains } : {}), ...(t.success.disaster ? { disaster: t.success.disaster } : {}),
            } } : {}),
    };
}
/** The ruling a test is made by: the system's test dice, its open throw and ties, or null when it names no test. */
export function testRuling(rules, borrowed) {
    if (!rules)
        return null;
    return { dice: testDiceData(rules.dice), open: rules.open ? testDiceData(rules.open) : null, ties: rules.ties, ...(borrowed ? { borrowed } : {}) };
}
/** What a system without success tests is told. */
export const NO_TEST_RULES = "This table's game system has no success test rules, so the table cannot make a test.";
const sum = (xs) => xs.reduce((a, b) => a + b, 0);
const minTargetOf = (dice) => dice.success?.minTarget ?? 1;
/** The TN a side is thrown against: its target, its modifiers and its default's, at least the system's least. */
export const tnOf = (dice, target, terms, defaulted) => Math.max(minTargetOf(dice), target + sum(terms.map((t) => t.value)) + (defaulted?.target ?? 0));
/** One side's concrete dice, by the ruling: the open throw (no TN), or the test's dice against its TN. Null when they do not fit. */
export function sideDice(f, s) {
    const t = parseTestDice(f.open ? f.rules.open : f.rules.dice);
    if (!t)
        return null;
    return testDice(t, { dice: s.dice, target: f.open ? null : s.tn });
}
/** A side's result, read from its dice alone: successes, or on an open test the kept chain's total; and a disaster. */
export function sideResult(f, s) {
    // The asking side with its Karma uses folded on (issue 1529).
    if ('sides' in f && f.karma?.length && s === f.sides[0]) {
        const k = karmaState(f);
        return { value: k.disaster ? 0 : k.successes + k.burned, disaster: k.disaster };
    }
    const d = sideDice(f, s);
    if (!d)
        return { value: 0, disaster: false };
    const disaster = poolDisaster(d, s.pool);
    const raw = poolResult(d, s.pool);
    if (f.open)
        return { value: Math.max(0, raw - (s.defaulted?.target ?? 0)), disaster };
    return { value: disaster ? 0 : raw, disaster };
}
/** An opposed test's winner, by its side's index: more successes win; a tie is no result (null). One side: null. */
export function testWinner(f) {
    if (f.sides.length !== 2)
        return null;
    const [a, b] = f.sides.map((s) => sideResult(f, s).value);
    return a === b ? null : a > b ? 0 : 1;
}
/** The roll's own faces: every side's, in order, then every chain its Karma uses threw (issue 1529). */
export const testFaces = (f) => [...f.sides.flatMap((s) => poolFaces(s.pool)), ...(f.karma ?? []).flatMap((k) => poolFaces(k.dice ?? []))];
export function karmaState(f) {
    const s = f.sides[0];
    let pool = s.pool.map((x) => ({ ...x, faces: [...x.faces] }));
    let dice = s.dice;
    let burned = 0;
    let avoided = false;
    let rerolls = 0;
    let bought = 0;
    const atLeast = (p) => { const d = sideDice(f, { dice: p.length, tn: s.tn }); return d ? p.map((x) => poolResult(d, [x]) > 0) : p.map(() => false); };
    for (const k of f.karma ?? []) {
        if (k.use === 'reroll_failures') {
            const ok = atLeast(pool);
            let i = 0;
            pool = pool.map((x, j) => (ok[j] ? x : k.dice?.[i++] ?? x));
            rerolls += 1;
        }
        else if (k.use === 'buy_dice') {
            pool = [...pool, ...(k.dice ?? [])];
            dice += k.dice?.length ?? 0;
            bought += 1;
        }
        else if (k.use === 'buy_success')
            burned += 1;
        else
            avoided = true;
    }
    const d = sideDice(f, { dice, tn: s.tn });
    const successes = d ? poolResult(d, pool) : 0;
    const disaster = d ? poolDisaster(d, pool) && !avoided : false;
    return { pool, dice, successes: disaster ? 0 : successes, failures: pool.length - successes, burned, disaster, avoided, rerolls, bought };
}
/** What a Karma use costs next (p. 246): a re-roll or a bought die one more than the last of its kind; a success or a disaster avoided, 1. */
export function karmaCost(f, use) {
    const k = karmaState(f);
    return use === 'reroll_failures' ? k.rerolls + 1 : use === 'buy_dice' ? k.bought + 1 : 1;
}
/** The words a Karma use is offered with (§5.2), and its line's verb. */
export const KARMA_WORDS = { reroll_failures: 'Re-roll the failures', buy_dice: 'Buy a die', buy_success: 'Burn 1 for a success', avoid_disaster: 'Avoid the disaster' };
/**
 * Whether a Karma use may be made on this test now (p. 246), or the refusal in words: a success test's, not an open
 * one's; nothing after a disaster avoided; a re-roll needs a failure; a bought die at most the rating's dice; a success
 * bought needs a success the dice made; a disaster avoided needs a disaster. `left`, what is left of the pool.
 */
export function karmaRefusal(f, use, left) {
    if (f.open)
        return 'Karma on an open test is the DM\'s to work out.';
    const k = karmaState(f);
    const s = f.sides[0];
    if (k.avoided)
        return 'A disaster avoided allows no more.';
    if (use === 'reroll_failures' && !k.failures)
        return 'There are no failures to re-roll.';
    if (use === 'buy_dice' && k.bought >= s.rated.rating)
        return `At most ${s.rated.rating} ${s.rated.rating === 1 ? 'die' : 'dice'} may be bought for this test.`;
    if (use === 'buy_dice' && k.dice >= MAX_POOL)
        return 'No more dice can be thrown.';
    if (use === 'buy_success' && (k.disaster || k.successes < 1))
        return 'A success can be bought only on a test that has one already.';
    if (use === 'avoid_disaster' && !k.disaster)
        return 'That test is not a disaster.';
    const cost = karmaCost(f, use);
    if (cost > left)
        return `That costs ${cost}, and ${left} ${left === 1 ? 'is' : 'are'} left.`;
    return null;
}
/** A Karma use made concrete: the chains it throws (the failures again, or one bought die), its cost stamped. */
export function makeKarma(f, use, pool, rng) {
    const k = karmaState(f);
    const s = f.sides[0];
    const t = parseTestDice(f.rules.dice);
    const n = use === 'reroll_failures' ? k.failures : use === 'buy_dice' ? 1 : 0;
    let dice;
    if (n) {
        const d = t ? testDice(t, { dice: n, target: s.tn }) : null;
        if (!d)
            return 'Those dice cannot be thrown.';
        dice = throwPool(d, rng);
    }
    const made = { use, key: pool.key, label: pool.label, cost: karmaCost(f, use), ...(dice ? { dice } : {}) };
    return { ...f, karma: [...(f.karma ?? []), made] };
}
/** One Karma use's line (§5.2): "Karma Pool: 4 failures re-rolled for 1: 3, 5, 5, 6→3 → 2 successes". */
function karmaLines(f) {
    const out = [];
    for (let i = 0; i < (f.karma?.length ?? 0); i++) {
        const k = f.karma[i];
        const now = sideResult({ ...f, karma: f.karma.slice(0, i + 1) }, f.sides[0]);
        const res = now.disaster ? 'disaster' : plural(now.value);
        const earlier = f.karma.slice(0, i).some((x) => x.use === k.use);
        const cost = `${k.cost}${earlier && (k.use === 'reroll_failures' || k.use === 'buy_dice') ? ' more' : ''}`;
        if (k.use === 'reroll_failures')
            out.push(`${k.label}: ${k.dice?.length ?? 0} ${k.dice?.length === 1 ? 'failure' : 'failures'} re-rolled for ${cost}: ${poolText(k.dice ?? [])} → ${res}`);
        else if (k.use === 'buy_dice')
            out.push(`${k.label}: a die bought for ${cost}: ${poolText(k.dice ?? [])} → ${res}`);
        else if (k.use === 'buy_success')
            out.push(`${k.label}: 1 burned for a success → ${res}`);
        else
            out.push(`${k.label}: 1 spent, the disaster is a failure`);
    }
    return out;
}
/**
 * Whether a default may be taken (§3.2.2, p. 84–85), or the refusal in words. The system's route to that kind; the TN
 * before the route's own modifier under the system's limit; and the lacked skill's links naming the rated one: a skill
 * of its own family for `skill`, the base skill of a specialization for `specialization`, an attribute for `attribute`.
 */
export function defaultRefusal(rules, d, rated, beforeDefault, web) {
    const route = rules.defaulting?.routes.find((r) => r.to === d.to);
    if (!route)
        return `This game system has no default to ${d.to === 'attribute' ? 'an attribute' : `a ${d.to}`}.`;
    const limit = rules.defaulting.limit;
    if (limit !== null && beforeDefault !== null && beforeDefault >= limit)
        return `No default is allowed at a TN of ${limit} or more.`;
    if (!web || web.ref !== d.skill)
        return `${d.name}'s defaults can't be read just now.`;
    if (!routesOf(web).includes(d.to) || !rated.ref || !(d.to === 'attribute' ? web.attributes : web.skills).includes(rated.ref))
        return `${d.name} does not default to ${rated.name}.`;
    return null;
}
const routesOf = (w) => (w.noDefault ? [] : [...(w.skills.length ? ['skill', 'specialization'] : []), ...(w.attributes.length ? ['attribute'] : [])]);
/**
 * A test made concrete (§5.1): each side thrown with the generator, by the system's rules. The asking side's default is
 * checked by `defaultRefusal` first (with the lacked skill's links, read by the caller). A string is a refusal in words.
 */
export function makeTest(req, rules, who, rng, web, borrowed, spend) {
    const ruling = testRuling(rules, borrowed);
    if (!rules || !ruling)
        return NO_TEST_RULES;
    if (req.open && !rules.open)
        return "This game system has no open test.";
    let defaulted;
    if (req.defaulted) {
        const beforeDefault = req.open ? null : req.target + sum(req.modifiers.map((t) => t.value));
        const no = defaultRefusal(rules, req.defaulted, req.rated, beforeDefault, web);
        if (no)
            return no;
        const route = rules.defaulting.routes.find((r) => r.to === req.defaulted.to);
        defaulted = { ...req.defaulted, target: route.target, pool: route.pool };
    }
    const throwSide = (s) => {
        // The dice: the rating, and the pool dice the server allowed (§5.2).
        const dice = s.rated.rating + (s.spent?.dice ?? 0);
        const tn = req.open ? null : tnOf(rules.dice, s.target, s.terms, s.defaulted);
        const d = testDice(req.open ? rules.open : rules.dice, { dice, target: tn });
        if (!d)
            return 'Those dice cannot be thrown.';
        return { ...s, dice, tn, pool: throwPool(d, rng) };
    };
    const first = throwSide({ ...who, rated: req.rated, ...(defaulted ? { defaulted } : {}), ...(spend ? { spent: { ...spend } } : {}), target: req.open ? null : req.target, terms: req.modifiers });
    if (typeof first === 'string')
        return first;
    const sides = [first];
    if (req.opposed) {
        const o = req.opposed;
        const second = throwSide({ name: o.name, rated: { kind: 'typed', name: o.name, rating: o.rating }, target: o.target, terms: o.modifiers });
        if (typeof second === 'string')
            return second;
        sides.push(second);
    }
    return { open: req.open, sides, rules: ruling };
}
/** The roll record's head: its label, its notation and total (the asking side's result). */
export function testLabel(f) {
    const r = f.sides[0].rated;
    return `${f.sides[0].defaulted?.name ?? r.name}${f.open ? ' (open)' : ''}${f.sides.length > 1 ? `, opposed by ${f.sides[1].name}` : ''} test`.slice(0, 80);
}
// ── Stored records ────────────────────────────────────────────────────────
function parseSide(v, f, rules, second) {
    if (!isObj(v) || !only(v, ['name', 'token', 'rated', 'defaulted', 'spent', 'dice', 'target', 'terms', 'tn', 'pool']))
        return null;
    if (v.name !== null && !isText(v.name, TEST_LIMITS.name))
        return null;
    if (v.token !== undefined && !UUID_RE.test(String(v.token)))
        return null;
    const rated = parseRated(v.rated);
    if (!rated || (second && (rated.kind !== 'typed' || v.defaulted !== undefined || v.token !== undefined)))
        return null;
    let defaulted;
    if (v.defaulted !== undefined) {
        const d = v.defaulted;
        const ask = parseDefaultAsk(isObj(d) ? { skill: d.skill, name: d.name, to: d.to, ...(d.base !== undefined ? { base: d.base } : {}) } : null);
        if (!ask || !isObj(d) || !only(d, ['skill', 'name', 'to', 'base', 'target', 'pool']) || !isInt(d.target, 0, 20) || !ROUTE_POOL.includes(d.pool) || rated.kind !== ask.to)
            return null;
        defaulted = { ...ask, target: d.target, pool: d.pool };
    }
    let spent;
    if (v.spent !== undefined) {
        const p = v.spent;
        if (second || !isObj(p) || !only(p, ['key', 'label', 'for', 'dice', 'cap']) || typeof p.key !== 'string' || !POOL_KEY_RE.test(p.key) || !isText(p.label, 60)
            || !POOL_FOR.includes(p.for) || !isInt(p.dice, 1, MAX_POOL) || !(p.cap === null || (isInt(p.cap, 0, MAX_POOL) && p.cap >= p.dice)))
            return null;
        spent = { key: p.key, label: p.label.trim(), for: p.for, dice: p.dice, cap: p.cap };
    }
    if (!isInt(v.dice, 1, MAX_POOL) || v.dice !== rated.rating + (spent?.dice ?? 0))
        return null;
    // One more than the asker may write: the wound term the server joins (issue 1526).
    const terms = parseTerms(v.terms, TEST_LIMITS.modifiers + 1);
    if (!terms)
        return null;
    if (f.open) {
        if (v.target !== null || v.tn !== null || terms.length)
            return null;
    }
    else if (!isInt(v.target, 1, TEST_LIMITS.target) || v.tn !== tnOf(rules.dice, v.target, terms, defaulted))
        return null;
    const side = { dice: v.dice, tn: v.tn };
    const d = sideDice(f, side);
    const pool = d ? parsePool(v.pool, d, 0) : null;
    if (!pool)
        return null;
    return {
        name: v.name, ...(typeof v.token === 'string' ? { token: v.token } : {}), rated, ...(defaulted ? { defaulted } : {}), ...(spent ? { spent } : {}),
        dice: v.dice, target: v.target, terms, tn: v.tn, pool,
    };
}
/** Stored facts: exact shape or null. Every side's chains must be what its dice and TN make. */
export function parseTestFacts(v) {
    if (!isObj(v) || !only(v, ['open', 'sides', 'rules', 'karma', 'conjure', 'matrix', 'vehicle', 'power']) || typeof v.open !== 'boolean' || !isObj(v.rules))
        return null;
    const r = v.rules;
    if (!only(r, ['dice', 'open', 'ties', 'borrowed']))
        return null;
    const dice = parseTestDice(r.dice);
    const open = r.open === null ? null : parseTestDice(r.open);
    if (!dice || !dice.success || (r.open !== null && (!open || open.success)) || !(r.ties === null || r.ties === 'none'))
        return null;
    if (r.borrowed !== undefined && !isText(r.borrowed, 120))
        return null;
    if (v.open && !open)
        return null;
    const rules = { dice: testDiceData(dice), open: open ? testDiceData(open) : null, ties: r.ties, ...(typeof r.borrowed === 'string' ? { borrowed: r.borrowed } : {}) };
    const f = { open: v.open, rules };
    if (!Array.isArray(v.sides) || v.sides.length < 1 || v.sides.length > (v.open ? 1 : 2))
        return null;
    const sides = [];
    for (const [i, s] of v.sides.entries()) {
        const got = parseSide(s, f, { dice, open }, i > 0);
        if (!got)
            return null;
        sides.push(got);
    }
    // A conjuring (issue 1531): one side, never open, no Karma uses; its drain and spirit read strictly.
    // A System Test (issue 1532): one side, never open, no Karma uses, no conjuring; the host's test read strictly.
    // A vehicle test (issue 1533): one side, never open, no Karma uses, nothing else beside it.
    // A spirit's power (issue 1172): never open, no Karma uses, nothing else beside it; its sides as the power's roll has them.
    if (v.power !== undefined) {
        const power = v.open || v.karma !== undefined || v.conjure !== undefined || v.matrix !== undefined || v.vehicle !== undefined ? null : parsePowerFacts(v.power, sides);
        return power ? { open: false, sides, rules, power } : null;
    }
    if (v.vehicle !== undefined) {
        const vehicle = v.open || sides.length !== 1 || v.karma !== undefined || v.conjure !== undefined || v.matrix !== undefined ? null : parseVehicleFacts(v.vehicle, sides[0]);
        return vehicle ? { open: false, sides, rules, vehicle } : null;
    }
    if (v.matrix !== undefined) {
        const matrix = v.open || sides.length !== 1 || v.karma !== undefined || v.conjure !== undefined ? null : parseMatrixFacts(v.matrix, rules.dice, sides[0]);
        return matrix ? { open: false, sides, rules, matrix } : null;
    }
    if (v.conjure !== undefined) {
        const conjure = v.open || sides.length !== 1 || v.karma !== undefined ? null : parseConjureFacts(v.conjure, rules.dice, sides[0]);
        return conjure ? { open: false, sides, rules, conjure } : null;
    }
    if (v.karma === undefined)
        return { open: v.open, sides, rules };
    // Karma uses (issue 1529): each what its place in the order makes it — its cost, the chains it threw.
    if (v.open || !Array.isArray(v.karma) || !v.karma.length || v.karma.length > KARMA_LIMITS.uses)
        return null;
    let k0 = { open: v.open, sides, rules };
    const t = parseTestDice(rules.dice);
    for (const x of v.karma) {
        if (!t || !isObj(x) || !only(x, ['use', 'key', 'label', 'cost', 'dice']) || !POOL_USES.includes(x.use) || typeof x.key !== 'string' || !POOL_KEY_RE.test(x.key) || !isText(x.label, 60))
            return null;
        const use = x.use;
        const k = karmaState(k0);
        if (k.avoided || x.cost !== karmaCost(k0, use))
            return null;
        const n = use === 'reroll_failures' ? k.failures : use === 'buy_dice' ? 1 : 0;
        let dice;
        if (n) {
            const d = testDice(t, { dice: n, target: sides[0].tn });
            const got = d ? parsePool(x.dice, d, 0) : null;
            if (!got)
                return null;
            dice = got;
        }
        else if (x.dice !== undefined)
            return null;
        if ((use === 'buy_success' && (k.disaster || k.successes < 1)) || (use === 'avoid_disaster' && !k.disaster))
            return null;
        k0 = { ...k0, karma: [...(k0.karma ?? []), { use, key: x.key, label: x.label.trim(), cost: x.cost, ...(dice ? { dice } : {}) }] };
    }
    return k0;
}
/** The most Karma uses one test carries. */
export const KARMA_LIMITS = { uses: 24 };
/** Whether a stored roll's dice and total are its test's: the faces of every side, the asking side's result. */
export const testRollMatches = (dice, total, f) => {
    const faces = testFaces(f);
    return dice.length === faces.length && dice.every((d, i) => d === faces[i]) && total === sideResult(f, f.sides[0]).value;
};
// ── The lines ─────────────────────────────────────────────────────────────
const signed = (n) => (n < 0 ? `− ${-n}` : `+ ${n}`);
const plural = (n) => `${n} success${n === 1 ? '' : 'es'}`;
/** One side's working line (§5.1): "Mara: Stealth 5 vs TN 5 (4 + 1 wounded): 1, 4, 5, 6, 3, 5, 2 → 3 successes". */
export function sideLine(f, s) {
    const who = s.name && s.name !== s.rated.name ? `${s.name}: ` : '';
    const what = s.defaulted ? `${s.defaulted.name}, defaulting to ${s.rated.name}` : s.rated.name;
    const res = sideResult(f, s);
    if (f.open) {
        const raw = res.value + (s.defaulted?.target ?? 0);
        const less = s.defaulted ? ` ${signed(-s.defaulted.target)} defaulting = ${res.value}` : '';
        // Every chain as thrown (§5.1): the highest is the result, so none is marked dropped.
        return `${who}${what} (open) ${ratedDice(s)}: ${poolText(s.pool.map((x) => ({ faces: x.faces })))} → ${raw}${less}`;
    }
    const parts = [...s.terms.map((t) => ` ${signed(t.value)} ${t.label}`), ...(s.defaulted ? [` ${signed(s.defaulted.target)} defaulting`] : [])];
    const asked = s.target + sum(s.terms.map((t) => t.value)) + (s.defaulted?.target ?? 0);
    const raised = asked !== s.tn ? `, at least ${s.tn}` : '';
    const working = parts.length || raised ? ` (${s.target}${parts.join('')}${raised})` : '';
    return `${who}${what} ${ratedDice(s)} vs TN ${s.tn}${working}: ${poolText(s.pool)} → ${res.disaster ? 'disaster' : plural(res.value)}`;
}
/** A side's dice in words (§5.1): its rating, and any pool dice it spent with the total ("5 + 2 Combat Pool = 7 dice"). */
const ratedDice = (s) => (s.spent ? `${s.rated.rating} + ${s.spent.dice} ${s.spent.label} = ${s.dice} dice` : `${s.rated.rating}`);
/** Every line of a test: each side's working, then an opposed test's outcome ("Mara wins by 2 successes" or "no result"). */
export function testLines(f) {
    if (f.conjure)
        return conjureLines(f);
    if (f.matrix)
        return matrixLines(f);
    if (f.vehicle)
        return vehicleLines(f);
    if (f.power)
        return powerLines(f);
    const lines = f.sides.map((s) => sideLine({ open: f.open, rules: f.rules }, s));
    // The asking side's Karma uses (issue 1529), a line each, below its own.
    if (f.karma?.length)
        lines.splice(1, 0, ...karmaLines(f));
    if (f.sides.length === 2) {
        const w = testWinner(f);
        const [a, b] = f.sides.map((s) => sideResult(f, s).value);
        const named = (s) => s.name ?? s.rated.name;
        lines.push(w === null ? 'A tie: no result.' : `${named(f.sides[w])} wins by ${plural(Math.abs(a - b))}.`);
    }
    return lines;
}
const specName = (skill, spec) => `${skill} (${spec})`.slice(0, TEST_LIMITS.name);
const attributeRef = (a, refs) => refs.find((r) => a.words.includes(r.split('/')[1]));
/**
 * What a character can test (§5.1), from its sheet and the skill web: each skill it has, each specialization, each
 * attribute; and each skill it lacks with the defaulting routes the system names and the skill's links allow, each with
 * its rating, TN modifier and pool cap (`half_rating`: half the default skill's rating; `half_base`: half the
 * specialization's base skill; `none`: no pool), rounded down. A skill with no links, or one the content marks as having
 * no default, offers none.
 */
export function testChoices(skills, attributes, web, rules) {
    const choices = [];
    for (const s of skills) {
        choices.push({ kind: 'skill', name: s.name, rating: s.rating, ...(s.ref ? { ref: s.ref } : {}) });
        if (s.spec)
            choices.push({ kind: 'specialization', name: specName(s.name, s.spec.name), rating: s.spec.rating, ...(s.ref ? { ref: s.ref } : {}) });
    }
    const attrRefs = [...new Set(web.flatMap((w) => w.attributes))];
    for (const a of attributes) {
        const ref = attributeRef(a, attrRefs);
        choices.push({ kind: 'attribute', name: a.name, rating: a.rating, ...(ref ? { ref } : {}) });
    }
    const had = new Set(skills.map((s) => s.ref).filter(Boolean));
    const routes = rules?.defaulting?.routes ?? [];
    const lacked = web.filter((w) => !had.has(w.ref)).map((w) => {
        const out = [];
        if (!w.noDefault) {
            for (const route of routes) {
                if (route.to === 'skill') {
                    for (const s of skills)
                        if (s.ref && w.skills.includes(s.ref))
                            out.push({ to: 'skill', rated: { kind: 'skill', name: s.name, rating: s.rating, ref: s.ref }, target: route.target, pool: route.pool, cap: capOf(route, s.rating, s.rating) });
                }
                else if (route.to === 'specialization') {
                    for (const s of skills)
                        if (s.ref && s.spec && w.skills.includes(s.ref))
                            out.push({ to: 'specialization', rated: { kind: 'specialization', name: specName(s.name, s.spec.name), rating: s.spec.rating, ref: s.ref }, target: route.target, pool: route.pool, cap: capOf(route, s.spec.rating, s.rating) });
                }
                else {
                    for (const a of attributes) {
                        const ref = attributeRef(a, w.attributes);
                        if (ref)
                            out.push({ to: 'attribute', rated: { kind: 'attribute', name: a.name, rating: a.rating, ref }, target: route.target, pool: route.pool, cap: capOf(route, a.rating, a.rating) });
                    }
                }
            }
        }
        return { ref: w.ref, name: w.name, routes: out };
    });
    return { choices, lacked };
}
const capOf = (route, rating, base) => (route.pool === 'none' ? null : Math.floor((route.pool === 'half_rating' ? rating : base) / 2));
//# sourceMappingURL=test.js.map